import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, ScanLine, Sparkles, ShieldCheck, ArrowRight, Star, Quote, Film } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { StarRating } from "../components/StarRating";
import { VideoEmbed } from "../components/VideoEmbed";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { ISSUE_LABELS } from "../lib/constants";

const HERO = "/hero-loc.jpg";

const steps = [
  { icon: ShieldCheck, title: "Pay & Begin", text: "A single R79 scan fee secures your private analysis." },
  { icon: ScanLine, title: "Capture Your Roots", text: "Upload a close-up of your scalp and loc roots." },
  { icon: Sparkles, title: "Receive Your Report", text: "Expert observations, product matches & aftercare, in seconds." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ average: 0, count: 0 });
  const [reviews, setReviews] = useState([]);
  const [transforms, setTransforms] = useState([]);

  useEffect(() => {
    api.get("/reviews/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data.reviews.slice(0, 4))).catch(() => {});
    api.get("/transformations").then((r) => setTransforms(r.data.transformations.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />

      <section className="relative mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-16 md:grid-cols-2 md:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex flex-col justify-center">
          <span className="mb-5 flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
            <Crown className="h-4 w-4" /> Carlocs Group · Loc Specialists
          </span>
          <h1 className="font-heading text-5xl font-500 leading-[1.05] tracking-tight text-cream md:text-6xl">
            The crown you wear<br />deserves real care.
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
            A scalp health analysis built exclusively for people with locs. Capture your roots,
            answer a few questions, and receive a precise report with product matches from the
            Carlocs collection.
          </p>

          {summary.count > 0 && (
            <div className="mt-6 flex items-center gap-3" data-testid="hero-rating">
              <StarRating value={Math.round(summary.average)} size={18} />
              <span className="text-sm text-cream">{summary.average}</span>
              <span className="text-sm text-muted-foreground">from {summary.count} verified review{summary.count === 1 ? "" : "s"}</span>
            </div>
          )}

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Button className="group rounded-full bg-gold px-7 py-6 text-base font-500 text-charcoal hover:bg-gold/90" onClick={() => navigate("/auth")} data-testid="hero-start-btn">
              Start Scalp Scan · R79
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <span className="text-xs text-muted-foreground">Observations, never a medical diagnosis.</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
          className="relative overflow-hidden rounded-2xl border border-charcoal-border">
          <img src={HERO} alt="Person with elegant locs" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-transparent" />
          <div className="absolute bottom-5 left-5 rounded-xl border border-white/10 bg-black/50 px-4 py-3 backdrop-blur-md">
            <p className="font-heading text-2xl text-gold">92<span className="text-cream/60 text-base">/100</span></p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-cream/70">Crown Score</p>
          </div>
        </motion.div>
      </section>

      {/* Steps */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }} className="rounded-xl border border-charcoal-border bg-charcoal-surface p-8">
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold">
                <s.icon className="h-5 w-5" strokeWidth={1.5} />
              </span>
              <h3 className="mb-2 font-heading text-2xl text-cream">{s.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Transformations preview */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold"><Film className="h-4 w-4" /> Real Transformations</span>
            <h2 className="mt-2 font-heading text-4xl text-cream">See the work, up close</h2>
          </div>
          <Button variant="ghost" className="rounded-full text-gold hover:text-cream" onClick={() => navigate("/gallery")} data-testid="landing-view-gallery">
            View all <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        {transforms.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {transforms.map((it) => (
              <div key={it.id} className="overflow-hidden rounded-xl border border-charcoal-border bg-charcoal-surface">
                <VideoEmbed url={it.embed_url} title={it.title} />
                <div className="p-5">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{ISSUE_LABELS[it.issue_tag] || it.issue_tag}</span>
                  <h3 className="mt-1 font-heading text-xl leading-tight text-cream">{it.title}</h3>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-charcoal-border bg-charcoal-surface py-20 text-center" data-testid="transformations-coming-soon">
            <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-gold/30 text-gold"><Film className="h-6 w-6" strokeWidth={1.4} /></span>
            <h3 className="font-heading text-3xl text-cream">Coming soon</h3>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">Real before &amp; after treatment videos are on their way. Watch this space.</p>
          </div>
        )}
      </section>

      {/* Reviews */}
      <section className="border-t border-charcoal-border bg-charcoal-surface/40 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold"><Star className="h-4 w-4" /> Client Reviews</span>
              <h2 className="mt-2 font-heading text-4xl text-cream">Loved by the crowned</h2>
            </div>
            {summary.count > 0 && (
              <div className="flex items-center gap-3">
                <span className="font-heading text-4xl text-gold">{summary.average}</span>
                <div><StarRating value={Math.round(summary.average)} size={16} /><p className="mt-1 text-xs text-muted-foreground">{summary.count} reviews</p></div>
              </div>
            )}
          </div>

          {reviews.length === 0 ? (
            <p className="text-muted-foreground">Be the first to review after your scan.</p>
          ) : (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              {reviews.map((rv) => (
                <div key={rv.id} className="rounded-xl border border-charcoal-border bg-charcoal p-6">
                  <Quote className="mb-3 h-5 w-5 text-gold/60" />
                  <p className="text-sm leading-relaxed text-cream/90">{rv.text || "Highly recommend."}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-sm text-cream">{rv.name}</p>
                    <StarRating value={rv.rating} size={13} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-10">
            <Button variant="ghost" className="rounded-full border border-charcoal-border text-cream hover:text-gold" onClick={() => navigate("/reviews")} data-testid="landing-all-reviews">
              Read all reviews <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
